import { ApplicationView } from '@aha-app/mvc';
import classNames from 'classnames';
import React, { useEffect } from 'react';

import { type WhiteboardSidePanelItem } from '../../sidepanel-types';
import WithHint from '../../toolbars/WithHint';
import { formatDate, formatTime } from '../../utils/time';
import historyStyles from './history.module.css';
import type HistoryManager from '../../controllers/HistoryManager';
import { svgToIcon } from '../../utils/svg';
import historyIcon from '../../assets/icons/history.svg';

const HistoryPanelContent: React.FC<{ historyManager: HistoryManager }> = ({
  historyManager,
}) => {
  const { whiteboardVersions, flashLatestVersion } = historyManager.state;

  useEffect(() => {
    void historyManager.actionGetWhiteboardVersions();
  }, []);

  if (!whiteboardVersions) {
    return <aha-loading-row rows={3}></aha-loading-row>;
  }

  return (
    <ul className={historyStyles.historyTimeline}>
      <li
        key='current'
        className={classNames(historyStyles.historyTimelineEntry, {
          [historyStyles.historyTimelineEntryActive]:
            historyManager.state.previewVersionId === null,
        })}
        onClick={() => {
          historyManager.actionClosePreview();
        }}
      >
        Current version
        {historyManager.allowedToRevert() && (
          <div className={historyStyles.smallRestoreButton}>
            <WithHint name='Save current version'>
              <aha-button
                size='mini'
                kind='ghost'
                title='Save current version so you can restore to it later'
                onClick={e => {
                  e.stopPropagation();
                  void historyManager.actionSaveVersion();
                }}
                style={{ marginTop: '-4px' }}
              >
                <i className='fa-regular fa-plus-large'></i>
              </aha-button>
            </WithHint>
          </div>
        )}
      </li>
      {whiteboardVersions.map((version, versionIndex) => {
        return (
          <li
            key={version.id}
            className={classNames(historyStyles.historyTimelineEntry, {
              [historyStyles.historyTimelineEntryActive]:
                version.id === historyManager.state.previewVersionId,
              [historyStyles.fetchingPreview]:
                historyManager.state.fetchingPreview,
              [historyStyles.flashBackground]:
                flashLatestVersion && versionIndex === 0,
            })}
            onClick={() => {
              if (historyManager.state.fetchingPreview) {
                return;
              }

              void historyManager.actionPreviewVersion(version.id);
            }}
          >
            {formatDate(version.updatedAt)}
            {historyManager.allowedToRevert() && (
              <div className={historyStyles.smallRestoreButton}>
                <WithHint name='Restore'>
                  <aha-button
                    size='mini'
                    kind='ghost'
                    title='Restore'
                    onClick={e => {
                      e.stopPropagation();
                      void historyManager.actionRevertVersion(version.id);
                    }}
                  >
                    <i className='fa-regular fa-clock-rotate-left'></i>
                  </aha-button>
                </WithHint>
              </div>
            )}
            <div className={historyStyles.historyTimelineTime}>
              {formatTime(version.updatedAt)}
            </div>
            <div>
              {version.authors.map(author => {
                return (
                  <aha-avatar
                    key={author.id}
                    name={author.name}
                    src={author.avatarUrl}
                  ></aha-avatar>
                );
              })}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const HistoryPanelTitle: React.FC<{ historyManager: HistoryManager }> = ({
  historyManager,
}) => {
  const { previewVersionId } = historyManager.state;

  if (previewVersionId && historyManager.allowedToRevert()) {
    return (
      <aha-button
        kind='primary'
        onClick={() => {
          void historyManager.actionRevertVersion(previewVersionId);
        }}
      >
        Restore
      </aha-button>
    );
  }

  return null;
};

const HistoryPanelTitleView = ApplicationView(HistoryPanelTitle);
const HistoryPanelContentView = ApplicationView(HistoryPanelContent);

// Information for the side panel for rendering the history panel
export const historySidePanelItem = (
  historyManager: HistoryManager
): WhiteboardSidePanelItem => {
  return {
    id: 'history',
    name: 'History',
    icon: svgToIcon(historyIcon),
    titleContent: <HistoryPanelTitleView historyManager={historyManager} />,
    content: <HistoryPanelContentView historyManager={historyManager} />,
    onClose: () => {
      historyManager.actionClosePreview();
    },
  };
};
